import { useCallback, useState } from 'react';

import { showToast } from '@/components/reusable/toast';
import { useConfirmCancellation, useReinstateCancellation } from '@/hooks/queries/cancellations/use-cancellation-mutations';

import { unitTitle } from '../copy';
import { isActiveRow, needsReview } from '../engine/api-shapes';
import { type ICancellationScope } from '../hooks/use-cancellation-scope';
import { type ICancellationCapabilities, type ICancellationRow } from '../types';
import ReinstateDialog from './ReinstateDialog';

interface IRowActions {
	row: ICancellationRow;
	scope: ICancellationScope;
	capabilities: ICancellationCapabilities;
	onOpen?: () => void;
	/** Called after a successful Confirm / Reinstate (e.g. to mark an alert seen). */
	onDone?: () => void;
	size?: 'sm' | 'xs';
}

/**
 * Open · Confirm · Reinstate. Candidates get Open only — the write actions are not rendered at
 * all rather than disabled, because there is nothing a candidate can do to enable them.
 */
const RowActions = ({ row, scope, capabilities, onOpen, onDone, size = 'sm' }: IRowActions) => {
	const confirm = useConfirmCancellation(scope);
	const reinstate = useReinstateCancellation(scope);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogError, setDialogError] = useState<string | null>(null);
	const confirmMutate = confirm.mutateAsync;
	const reinstateMutate = reinstate.mutateAsync;

	const active = isActiveRow(row);
	const canConfirm = scope.canWrite && capabilities.review && needsReview(row);
	const canReinstate = scope.canWrite && active;

	const onConfirm = useCallback(async () => {
		const outcome = await confirmMutate(row.id);
		if (outcome.kind === 'done') {
			showToast('success', `Confirmed: ${unitTitle(row)}`);
			onDone?.();
		} else if (outcome.kind === 'notActive')
			showToast('error', 'This unit is no longer cancelled — the list has been refreshed.');
		else if (outcome.kind === 'forbidden') showToast('error', 'You cannot confirm cancellations for this party.');
		else showToast('error', outcome.message);
	}, [confirmMutate, row, onDone]);

	const onReinstate = useCallback(
		async (reason: string) => {
			setDialogError(null);
			const outcome = await reinstateMutate({ id: row.id, reason, v2: capabilities.version === 'v2' });
			if (outcome.kind === 'done') {
				setDialogOpen(false);
				showToast('success', `Reinstated: ${unitTitle(row)}`);
				onDone?.();
			} else if (outcome.kind === 'notActive') {
				setDialogError('This unit is no longer cancelled — someone else already changed it.');
			} else if (outcome.kind === 'forbidden') {
				setDialogError('You cannot reinstate units for this party.');
			} else setDialogError(outcome.message);
		},
		[reinstateMutate, row, capabilities.version, onDone]
	);

	const btn = size === 'xs' ? 'pc-btn--xs' : '';

	return (
		<div className='pc-actions'>
			{onOpen ? (
				<button
					type='button'
					className={`pc-btn ${btn}`}
					onClick={onOpen}
				>
					Open
				</button>
			) : null}
			{canConfirm ? (
				<button
					type='button'
					className={`pc-btn pc-btn--primary ${btn}`}
					onClick={() => void onConfirm()}
					disabled={confirm.isPending}
				>
					{confirm.isPending ? 'Confirming…' : 'Confirm'}
				</button>
			) : null}
			{canReinstate ? (
				<button
					type='button'
					className={`pc-btn pc-btn--danger-outline ${btn}`}
					onClick={() => {
						setDialogError(null);
						setDialogOpen(true);
					}}
				>
					Reinstate
				</button>
			) : null}
			{dialogOpen ? (
				<ReinstateDialog
					unitLabel={unitTitle(row)}
					reasonStored={capabilities.version === 'v2'}
					pending={reinstate.isPending}
					error={dialogError}
					onCancel={() => setDialogOpen(false)}
					onConfirm={(reason) => void onReinstate(reason)}
				/>
			) : null}
		</div>
	);
};

export default RowActions;
